import { Rooster } from "GameObjects/StoryGame/ch1/Rooster";
import { SceneContainer } from "GameObjects/StoryGame/SceneContainer";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Elastic, Expo, TimelineLite, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import * as Topics from "Data/Topics";
import * as Constants from "Data/Constants";

import { ch1_p0102 } from "../../../layouts/ch1_p01-02/ch1_p01-02";

export class Chapter1Page1 extends AbstractStoryState {

	readonly sky = this.addChild(new SceneSprite());
	readonly treeBack = this.addChild(new SceneSprite());
	readonly bushes = this.addChild(new SceneSprite());
	readonly houseBack = this.addChild(new SceneSprite());
	readonly houseFront = this.addChild(new SceneSprite());
	readonly treeFront = this.addChild(new SceneSprite());
	readonly fence = this.addChild(new SceneSprite());
	readonly rooster = this.addChild(new Rooster());
	readonly lightLeak = this.addChild(new SceneSprite());
	readonly frame = this.addChild(new SceneSprite());
	readonly lineArt = this.addChild(new SceneSprite());

	protected roosterClickCount = 0;
	protected readonly clicksRequired = 2;

	public init(pSize: PIXI.Point, pData: any) {
		// exported layout from Animate CC
		ch1_p0102.call(this);

		// interaction
		this._subscriptions.push(PubSub.subscribe("rooster clicked", this.onRoosterClicked.bind(this)));
		this._subscriptions.push(PubSub.subscribe("rooster animationcomplete", this.onRoosterAnimationComplete.bind(this)));

		// super.init calls onResize
		super.init(pSize, pData);
	}

	public update(pDeltaTime: number) {
		super.update(pDeltaTime);
		this.rooster.update(pDeltaTime);
	}

	public animateIn(pOnComplete: () => void) {
		super.animateIn(pOnComplete);

		const d = 7;
		const t = new TimelineLite();
		const ease = Expo.easeOut;

		const center = { x: 0.5, y: 0.75 };
		const containerPos = {
			x: (Constants.SCENE_WIDTH * center.x),
			y: (Constants.SCENE_HEIGHT * center.y),
		};

		this.frame.z = 7;
		this.sky.z = 7;
		// set lines and tree to same z depth
		this.lineArt.z = 6;
		this.treeBack.z = 6;
		this.bushes.z = 5;
		this.houseBack.z = 4;
		this.houseFront.z = 3;
		this.treeFront.z = 2;
		// set rooster and fence to same z depth
		this.fence.z = 1;
		this.rooster.z = 1;

		const childrenToAnimate = [
			this.frame,
			this.sky,
			this.lineArt,
			this.treeBack,
			this.bushes,
			this.houseBack,
			this.houseFront,
			this.treeFront,
			this.fence,
			this.rooster,
		]

		// parallax
		for (let i = childrenToAnimate.length - 1; i >= 0; --i) {
			const siblingIndex = this.getChildIndex(childrenToAnimate[i]);
			const child = this.removeChild(childrenToAnimate[i]);
			const container = this.addChildAt(new PIXI.Container(), siblingIndex);
			container.name = child.name + "_parallax";
			container.position.set(containerPos.x, containerPos.y);
			child.setParent(container);
			HLF.PointUtils.subtractFromPoint(child.position, container.position);
			const fromScale = 1 + ((8 - child.z) / 4); // TODO
			t.fromTo(container, d, { pixi: { scale: fromScale } }, { pixi: { scale: 1 }, ease }, 0);
		};

		// fade in watercolor edges
		t.from(
			[this.lightLeak, this.frame, this.lineArt],
			d,
			{ pixi: { alpha: 0 } },
			0,
		);

		t.call(()=>{
			console.log('parallax done');
		});

		// background music
		const intro = Application.instance.audio.createAudioTrack("HH_Ch1_Loop_A_Intro_v2", HLF.AudioCategory.BGM);
		const mainLoop = Application.instance.audio.createAudioTrack("HH_Ch1_Loop_A_Loop_v2", HLF.AudioCategory.BGM);
		mainLoop.setLooped(true);
		intro.once(HLF.HowlerUtils.Events.PLAY, () => {
			HLF.LogUtils.log("music started", {className: this.name, color: HLF.LogUtils.COLOR_RELISH});
			const bpm = 151; // beats per minute
			const bps = bpm / 60; // beats per second
			const spb = 1 / bps; // seconds per beat
			const beats = 4 * 11; // 11 bars of 4/4
			const seconds = beats * spb;
			TweenLite.delayedCall(seconds, () => {
				mainLoop.play();
			});
		})
		intro.once(HLF.HowlerUtils.Events.END, () => {
			intro.unloadSource();
		});
		intro.play();
	}

	protected onRoosterClicked() {
		this.hideHint();
		this.roosterClickCount++;
		HLF.LogUtils.log("rooster clicked %s times", {className: this.name, color: HLF.LogUtils.COLOR_RELISH}, this.roosterClickCount);
		if (this.roosterClickCount < this.clicksRequired) {
			this.showHint({ x: 500, y: 500 }, "hand", 8);
		}
	}

	protected onRoosterAnimationComplete() {
		if (this.roosterClickCount === this.clicksRequired) {
			this.gotoNextState({ transition: "left" });
		}
	}

	protected onAnimateInComplete() {
		super.onAnimateInComplete();
		// show hint 8 seconds after parallax is done
		this.showHint({ x: 500, y: 500 }, "hand", 14);
	}
}